import { Button } from 'primereact/button';
import React, { FormEvent } from 'react';
import { NoAuthPageTemplate } from '../../layout/NoAuthPageTemplate/NoAuthPageTemplate';
import { useForm } from '../../hooks/useForm';
import { LinkButton } from '../../components/LinkButton/LinkButton';
import { classNames } from 'primereact/utils';
import { Password } from 'primereact/password';
import { useClient } from '../../hooks/useClient';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import { PASSWORD_FORM_VALIDATORS } from './validators';
import { FormField } from '../../components/FormField/FormField';
import { User } from '../../types/user';
import { usePopups } from '../../context/PopupContext';
import { useNavigateScroll } from '../../hooks/useNavigateScroll';

interface PasswordForm {
  password: string;
  confirmPassword: string;
}

const INITIAL_PASSWORD_FORM: PasswordForm = {
  confirmPassword: '',
  password: '',
};

export const Join = () => {
  const { showToast } = usePopups();
  const navigate = useNavigateScroll();
  const { userId } = useParams();
  const { post, get } = useClient();
  const { form, setFormFields, isSaving, setIsSaving, hasChanged, validate, validationErrors } = useForm<PasswordForm>(
    INITIAL_PASSWORD_FORM,
    true,
    PASSWORD_FORM_VALIDATORS,
  );
  const { confirmPassword, password } = form;

  const fetcher = async (url: string) => {
    const response = await get<User>(url, {
      errorMessages: {
        summary: 'Error en la invitación',
        defaultDetail: 'La invitación no es válida.',
        // The code wins over the catalog text, which tells the user to reload a page
        USER_NOT_FOUND: 'La invitación no es válida.',
        USER_ALREADY_REGISTERED: 'Ya eres usuario de No Gravity. Inicia sesión o recupera tu contraseña.',
      },
      handlers: {
        defaultError: () => navigate('/invalid-invitation'),
      },
    });
    if (response && !response.isInvitation) {
      navigate('/login');
      showToast?.({
        severity: 'error',
        summary: 'Error en enlace de invitación',
        detail: 'Este enlace de invitación ya no es válido.',
      });
    }
    return response?.email;
  };

  // The route is /join/:userId, so the id is always there.
  useSWR(`/users/verify-invitation/${userId}`, fetcher);

  const recoverPassword = async () => {
    await post(`/users/set-initial-password/${userId}`, {
      body: form,
      errorMessages: {
        summary: 'Error al crear contraseña',
        defaultDetail: 'No se ha podido crear la contraseña.',
        USER_NOT_FOUND: 'La invitación ya no es válida.',
      },
      successMessage: {
        summary: 'Contraseña creada',
        detail: 'Se ha creado la contraseña. Puedes iniciar sesión con ella.',
      },
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const newErrors = validate();
    if (!form || newErrors) return;
    setIsSaving(true);
    await recoverPassword();
    setIsSaving(false);
    navigate('/');
  };

  return (
    <NoAuthPageTemplate>
      <h2>Has recibido una invitación a No Gravity</h2>
      <p>Solo tienes que elegir tu contraseña y podrás comenzar a utilizar nuestro panel de control.</p>
      <form
        className={classNames({
          'form-loading': isSaving,
        })}
        onSubmit={handleSubmit}
      >
        <FormField
          labelTitle='Contraseña'
          elementId='password'
          error={validationErrors?.password}
          errorMessageOverride='La contraseña debe tener al menos 8 caracteres, una mayúscula, una minúscula, un número y un carácter especial.'
        >
          <Password
            inputId='password'
            feedback={false}
            value={password}
            placeholder='Introduce tu contraseña'
            onChange={(e) => setFormFields({ password: e.target.value })}
            toggleMask
            className={classNames({ 'p-invalid': validationErrors?.password })}
          />
        </FormField>

        <FormField
          labelTitle='Confirmar contraseña'
          elementId='confirmPassword'
          error={validationErrors?.confirmPassword}
          errorPosition='static'
        >
          <Password
            inputId='confirmPassword'
            feedback={false}
            value={confirmPassword}
            placeholder='Vuelve a introducir la nueva contraseña'
            onChange={(e) => setFormFields({ confirmPassword: e.target.value })}
            toggleMask
            className={classNames({
              'p-invalid': validationErrors?.confirmPassword,
            })}
          />
        </FormField>

        <Button type='submit' label='Cambiar contraseña' className='full-width large' disabled={!hasChanged} loading={isSaving} />
        <LinkButton to='/' icon='pi pi-arrow-left'>
          Ir a inicio de sesión
        </LinkButton>
      </form>
    </NoAuthPageTemplate>
  );
};
